import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  MessageCircle, Users, Search, Download, Trash2, Eye,
  Calendar, User, BarChart3, ChevronLeft, ChevronRight,
  MessageSquare, File, RefreshCw, ArrowRight, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminChatMonitor = ({ user, onLogout }) => {
  const [stats, setStats] = useState({
    total_conversations: 0,
    total_groups: 0,
    total_one_on_one: 0,
    total_messages: 0,
    total_files: 0,
    today_messages: 0,
    most_active_users: []
  });
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  
  // Chat View Modal
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatViewData, setChatViewData] = useState(null);
  const [chatViewLoading, setChatViewLoading] = useState(false);
  
  // Filters
  const [filterUser, setFilterUser] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchStats(),
        fetchConversations(),
        fetchAllUsers()
      ]);
    } catch (err) {
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/chat/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      setStats(response.data || {
        total_conversations: 0,
        total_groups: 0,
        total_one_on_one: 0,
        total_messages: 0,
        total_files: 0,
        today_messages: 0,
        most_active_users: []
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Don't show toast for stats error, just use default values
    }
  };

  const fetchConversations = async (page = 1) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, page_size: 20 },
        timeout: 10000
      });
      setConversations(response.data.conversations || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
      setConversations([]);
    }
  };

  const fetchMessages = async (page = 1) => {
    try {
      const token = localStorage.getItem('token');
      const params = { page, page_size: 50 };
      
      if (selectedConversation) params.conversation_id = selectedConversation.id;
      if (filterUser && filterUser !== 'all') params.user_email = filterUser;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (searchQuery) params.search = searchQuery;
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/chat/messages`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        timeout: 10000
      });
      setMessages(response.data.messages || []);
      setTotalPages(response.data.total_pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setMessages([]);
      toast.error('Failed to fetch messages');
    }
  };

  const fetchAllUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAllUsers([]);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/chat/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message deleted');
      fetchMessages(currentPage);
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (selectedConversation) params.append('conversation_id', selectedConversation.id);
      if (filterUser && filterUser !== 'all') params.append('user_email', filterUser);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/chat/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `chat_export_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Chat history exported');
    } catch (error) {
      toast.error('Failed to export chat history');
    }
  };

  const viewConversationMessages = (conv) => {
    setSelectedConversation(conv);
    setActiveTab('messages');
    setCurrentPage(1);
  };

  // View full chat in modal
  const viewFullChat = async (conversationId) => {
    setChatViewLoading(true);
    setShowChatModal(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/chat/conversation/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000
      });
      setChatViewData(response.data);
    } catch (error) {
      console.error('Failed to load chat:', error);
      toast.error('Failed to load conversation');
      setShowChatModal(false);
    } finally {
      setChatViewLoading(false);
    }
  };

  const closeChatModal = () => {
    setShowChatModal(false);
    setChatViewData(null);
  };

  useEffect(() => {
    if (activeTab === 'messages') {
      fetchMessages(1);
    }
  }, [activeTab, selectedConversation]);

  const handleSearch = () => {
    fetchMessages(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
            <p className="text-slate-600">Loading chat data...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <Sidebar user={user} onLogout={onLogout} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadInitialData}>Retry</Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <Sidebar user={user} onLogout={onLogout} />
      
      <main className="flex-1 p-8 overflow-auto" data-testid="admin-chat-monitor">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 bg-clip-text text-transparent">
                Chat Monitoring
              </h1>
              <p className="text-slate-600 flex items-center gap-2 mt-1">
                <Eye className="w-4 h-4 text-blue-500" />
                Monitor all conversations, messages, and chat activity
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadInitialData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleExport}
                className="bg-gradient-to-r from-emerald-600 to-emerald-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b pb-2">
            {['overview', 'conversations', 'messages'].map((tab) => (
              <Button
                key={tab}
                variant={activeTab === tab ? 'default' : 'ghost'}
                onClick={() => setActiveTab(tab)}
                className={activeTab === tab ? 'bg-blue-600' : ''}
              >
                {tab === 'overview' && <BarChart3 className="w-4 h-4 mr-2" />}
                {tab === 'conversations' && <MessageCircle className="w-4 h-4 mr-2" />}
                {tab === 'messages' && <MessageSquare className="w-4 h-4 mr-2" />}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-500 rounded-xl">
                        <MessageCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Conversations</p>
                        <p className="text-2xl font-bold">{stats.total_conversations || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-purple-500 rounded-xl">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Group Chats</p>
                        <p className="text-2xl font-bold">{stats.total_groups || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-50 to-emerald-100">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-emerald-500 rounded-xl">
                        <MessageSquare className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Messages</p>
                        <p className="text-2xl font-bold">{stats.total_messages || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-amber-100">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-amber-500 rounded-xl">
                        <File className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Files Shared</p>
                        <p className="text-2xl font-bold">{stats.total_files || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Today's Activity & Most Active Users */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      Today's Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-8">
                      <p className="text-5xl font-bold text-blue-600">{stats.today_messages || 0}</p>
                      <p className="text-slate-500 mt-2">Messages sent today</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-500" />
                      Most Active Users
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(stats.most_active_users || []).length === 0 ? (
                        <p className="text-slate-400 text-center py-4">No activity yet</p>
                      ) : (
                        (stats.most_active_users || []).slice(0, 5).map((u, index) => (
                          <div key={u.email || index} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-600' : 'bg-slate-300'
                              }`}>
                                {index + 1}
                              </span>
                              <span className="font-medium">{u.email}</span>
                            </div>
                            <span className="text-slate-500">{u.message_count} messages</span>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Conversations Tab */}
          {activeTab === 'conversations' && (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-0">
                {conversations.length === 0 ? (
                  <div className="p-8 text-center text-slate-400">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Name/Participants</TableHead>
                        <TableHead>Messages</TableHead>
                        <TableHead>Last Activity</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {conversations.map((conv) => (
                        <TableRow key={conv.id}>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              conv.type === 'group' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {conv.type === 'group' ? 'Group' : 'Direct'}
                            </span>
                          </TableCell>
                          <TableCell>
                            {conv.type === 'group' ? conv.name : (conv.participants || []).join(', ')}
                          </TableCell>
                          <TableCell>{conv.message_count || 0}</TableCell>
                          <TableCell>{formatDate(conv.last_message_at)}</TableCell>
                          <TableCell>{formatDate(conv.created_at)}</TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => viewConversationMessages(conv)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="space-y-4">
              {/* Filters */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Select value={filterUser} onValueChange={setFilterUser}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by user" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        {allUsers.map((u) => (
                          <SelectItem key={u.email} value={u.email}>
                            {u.agent_name || u.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Input
                      type="date"
                      placeholder="From date"
                      value={filterDateFrom}
                      onChange={(e) => setFilterDateFrom(e.target.value)}
                    />
                    
                    <Input
                      type="date"
                      placeholder="To date"
                      value={filterDateTo}
                      onChange={(e) => setFilterDateTo(e.target.value)}
                    />
                    
                    <Input
                      placeholder="Search messages..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    
                    <Button onClick={handleSearch}>
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                  </div>
                  
                  {selectedConversation && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-center justify-between">
                      <span className="text-sm text-blue-700">
                        Viewing: {selectedConversation.type === 'group' ? selectedConversation.name : (selectedConversation.participants || []).join(' & ')}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedConversation(null)}
                      >
                        Clear filter
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Messages Table */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-0">
                  {messages.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No messages found</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date/Time</TableHead>
                          <TableHead>From → To</TableHead>
                          <TableHead>Message</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {messages.map((msg) => (
                          <TableRow key={msg.id} className={msg.deleted ? 'opacity-50' : 'hover:bg-slate-50'}>
                            <TableCell className="whitespace-nowrap text-sm">
                              {formatDate(msg.created_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-blue-600">
                                  {msg.sender_name || msg.sender_email?.split('@')[0]}
                                </span>
                                <ArrowRight className="w-4 h-4 text-slate-400" />
                                <span className={`font-medium ${msg.conversation_type === 'group' ? 'text-purple-600' : 'text-emerald-600'}`}>
                                  {msg.recipient_name || msg.conversation_name || 'Unknown'}
                                </span>
                                {msg.conversation_type === 'group' && (
                                  <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">
                                    Group
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="max-w-[350px]">
                              {msg.deleted ? (
                                <span className="italic text-slate-400">Message deleted</span>
                              ) : (
                                <span className="truncate block text-sm">{msg.content}</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                msg.type === 'file' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                              }`}>
                                {msg.type || 'text'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => viewFullChat(msg.conversation_id)}
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                  title="View full chat"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {!msg.deleted && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteMessage(msg.id)}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                    title="Delete message"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">Page {currentPage} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchMessages(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchMessages(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <Footer />
      </main>

      {/* Chat View Modal */}
      {showChatModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between border-b bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-t-lg">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  {chatViewLoading ? 'Loading...' : (
                    chatViewData?.conversation?.type === 'group' 
                      ? `Group: ${chatViewData?.conversation?.name}`
                      : `Chat: ${Object.values(chatViewData?.conversation?.participant_names || {}).join(' & ')}`
                  )}
                </CardTitle>
                {chatViewData && (
                  <p className="text-sm text-blue-100 mt-1">
                    {chatViewData.total_messages} messages • 
                    {chatViewData.conversation?.type === 'group' ? ` ${chatViewData.conversation?.participants?.length} members` : ' Direct chat'}
                  </p>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={closeChatModal} className="text-white hover:bg-white/20">
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden p-0">
              {chatViewLoading ? (
                <div className="flex items-center justify-center h-64">
                  <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : chatViewData?.messages?.length === 0 ? (
                <div className="flex items-center justify-center h-64 text-slate-400">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No messages in this conversation</p>
                  </div>
                </div>
              ) : (
                <ScrollArea className="h-[60vh] p-4">
                  <div className="space-y-3">
                    {chatViewData?.messages?.map((msg, index) => {
                      const isDeleted = msg.deleted;
                      const showDateHeader = index === 0 || 
                        new Date(msg.created_at).toDateString() !== 
                        new Date(chatViewData.messages[index - 1].created_at).toDateString();
                      
                      return (
                        <div key={msg.id}>
                          {showDateHeader && (
                            <div className="flex items-center justify-center my-4">
                              <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs rounded-full">
                                {new Date(msg.created_at).toLocaleDateString([], { 
                                  weekday: 'long', 
                                  year: 'numeric', 
                                  month: 'long', 
                                  day: 'numeric' 
                                })}
                              </span>
                            </div>
                          )}
                          <div className={`flex ${isDeleted ? 'opacity-50' : ''}`}>
                            <div className="flex-1">
                              <div className="inline-block max-w-[80%] rounded-2xl px-4 py-2 bg-white border shadow-sm">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-blue-600 text-sm">
                                    {msg.sender_name || msg.sender_email}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {formatTime(msg.created_at)}
                                  </span>
                                </div>
                                {isDeleted ? (
                                  <p className="italic text-slate-400 text-sm">Message deleted by admin</p>
                                ) : msg.type === 'file' ? (
                                  <a 
                                    href={`${BACKEND_URL}${msg.file_url}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-blue-600 hover:underline"
                                  >
                                    <File className="w-4 h-4" />
                                    {msg.file_name || 'Download file'}
                                  </a>
                                ) : (
                                  <p className="text-slate-800 whitespace-pre-wrap break-words">{msg.content}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminChatMonitor;
